import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Download, X, Smartphone, RefreshCw, Bell, BellOff, Wifi, WifiOff, Check } from 'lucide-react';
import { BRAND } from '../config/branding';

/**
 * Composant PWA Install Prompt amélioré
 * Affiche une bannière d'installation et gère les mises à jour
 */
export const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Vérifier si déjà installée
    if (window.matchMedia('(display-mode: standalone)').matches || 
        window.navigator.standalone === true) {
      setIsInstalled(true);
      return;
    }

    // Détecter iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Écouter l'événement d'installation disponible
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Attendre un peu avant d'afficher le prompt
      const dismissed = localStorage.getItem('pwa-prompt-dismissed');
      const dismissedTime = localStorage.getItem('pwa-prompt-dismissed-time');
      
      // Réafficher après 7 jours si refusé
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (!dismissed || (dismissedTime && Date.now() - parseInt(dismissedTime) > sevenDays)) {
        setTimeout(() => setShowPrompt(true), 4000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Pour iOS, afficher après un délai si pas installé
    if (isIOSDevice && !window.navigator.standalone) {
      const dismissed = localStorage.getItem('pwa-prompt-dismissed');
      const dismissedTime = localStorage.getItem('pwa-prompt-dismissed-time');
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      
      if (!dismissed || (dismissedTime && Date.now() - parseInt(dismissedTime) > sevenDays)) {
        setTimeout(() => setShowPrompt(true), 6000);
      }
    }

    // Écouter l'installation réussie
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          setIsInstalled(true);
        }
        setDeferredPrompt(null);
      } catch (error) {
        console.error('[PWA] Erreur installation:', error);
      }
    }
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
    localStorage.setItem('pwa-prompt-dismissed-time', Date.now().toString());
  };

  const handleShowIOSInstructions = () => {
    setShowIOSInstructions(true);
  };

  if (isInstalled || !showPrompt) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-50 animate-slide-up">
      <div className="bg-[#1E293B] border border-[#B69D74]/30 rounded-xl p-4 shadow-2xl backdrop-blur-sm">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-gradient-to-br from-[#B69D74]/30 to-[#B69D74]/10 rounded-lg shrink-0">
            <Smartphone className="w-6 h-6 text-[#B69D74]" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-white mb-1">
              Installer {BRAND.name}
            </h3>
            <p className="text-xs text-[#94A3B8] mb-3">
              {isIOS 
                ? "Ajoutez à l'écran d'accueil pour un accès instantané"
                : "Installez l'app pour y accéder même hors ligne"
              }
            </p>
            
            {isIOS ? (
              showIOSInstructions ? (
                <div className="text-xs text-[#94A3B8] bg-black/30 rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="flex items-center justify-center w-5 h-5 bg-[#B69D74]/20 rounded text-[10px] font-bold text-[#B69D74]">1</span>
                    <span>Appuyez sur le bouton <span className="inline-flex items-center justify-center w-5 h-5 bg-blue-500/20 rounded mx-1">
                      <svg className="w-3 h-3 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      </svg>
                    </span> Partager</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="flex items-center justify-center w-5 h-5 bg-[#B69D74]/20 rounded text-[10px] font-bold text-[#B69D74]">2</span>
                    <span>"Sur l'écran d'accueil"</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="flex items-center justify-center w-5 h-5 bg-[#B69D74]/20 rounded text-[10px] font-bold text-[#B69D74]">3</span>
                    <span>Confirmez "Ajouter"</span>
                  </div>
                </div>
              ) : (
                <Button
                  onClick={handleShowIOSInstructions}
                  className="w-full h-9 bg-[#B69D74] text-[#0F172A] hover:bg-[#C5AE87] text-xs font-semibold gap-2"
                >
                  Voir les instructions
                </Button>
              )
            ) : (
              <div className="flex gap-2">
                <Button
                  onClick={handleInstall}
                  className="flex-1 h-9 bg-[#B69D74] text-[#0F172A] hover:bg-[#C5AE87] text-xs font-semibold gap-2"
                >
                  <Download className="w-4 h-4" />
                  Installer
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleDismiss}
                  className="h-9 w-9 p-0 text-[#94A3B8] hover:text-white hover:bg-white/10"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
          
          {isIOS && (
            <Button
              variant="ghost"
              onClick={handleDismiss}
              className="h-6 w-6 p-0 text-[#94A3B8] hover:text-white -mt-1 -mr-1"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Bannière de mise à jour disponible
 */
export const UpdateBanner = ({ onUpdate, onDismiss }) => {
  const [updating, setUpdating] = useState(false);

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      await onUpdate?.();
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-[#B69D74] to-[#D4B88A] text-[#0F172A] py-2 px-4 safe-top">
      <div className="max-w-lg mx-auto flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs sm:text-sm font-medium">
          <RefreshCw className={`w-4 h-4 ${updating ? 'animate-spin' : ''}`} />
          <span>Nouvelle version disponible</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleUpdate}
            disabled={updating}
            size="sm"
            className="h-7 px-3 bg-[#0F172A] text-white hover:bg-[#1E293B] text-xs"
          >
            {updating ? 'Mise à jour...' : 'Mettre à jour'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDismiss}
            className="h-7 w-7 p-0 text-[#0F172A] hover:bg-black/10"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Hook pour détecter si l'app est en mode PWA
 */
export const usePWA = () => {
  const [isPWA, setIsPWA] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    // Détecter mode standalone (PWA installée)
    const checkStandalone = () => {
      setIsPWA(
        window.matchMedia('(display-mode: standalone)').matches ||
        window.matchMedia('(display-mode: fullscreen)').matches ||
        window.navigator.standalone === true
      );
    };
    
    checkStandalone();

    // Écouter les changements
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', checkStandalone);
    }

    // Écouter les changements de connexion
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', checkStandalone);
      }
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { isPWA, isOnline };
};

/**
 * Indicateur de statut offline amélioré
 */
export const OfflineIndicator = () => {
  const { isOnline } = usePWA();
  const [show, setShow] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setShow(true);
      setWasOffline(true);
    } else if (wasOffline) {
      // Afficher brièvement le retour en ligne
      setShow(true);
      const timer = setTimeout(() => {
        setShow(false);
        setWasOffline(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline]);

  if (!show) return null;

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 py-2 px-4 text-center text-xs font-medium safe-top transition-colors duration-300 ${
      isOnline 
        ? 'bg-emerald-500 text-white' 
        : 'bg-amber-500 text-black'
    }`}>
      <div className="flex items-center justify-center gap-2">
        {isOnline ? (
          <>
            <Wifi className="w-4 h-4" />
            <span>Connexion rétablie</span>
            <Check className="w-4 h-4" />
          </>
        ) : (
          <>
            <WifiOff className="w-4 h-4" />
            <span>Mode hors ligne - Certaines fonctionnalités peuvent être limitées</span>
          </>
        )}
      </div>
    </div>
  );
};

/**
 * Bouton pour activer les notifications
 */
export const NotificationToggle = ({ enabled, onToggle, loading }) => {
  return (
    <Button
      onClick={onToggle}
      disabled={loading}
      variant="ghost"
      size="sm"
      className={`h-8 gap-2 text-xs ${
        enabled 
          ? 'text-[#B69D74] hover:text-[#C5AE87]' 
          : 'text-[#64748B] hover:text-[#94A3B8]'
      }`}
    >
      {enabled ? (
        <>
          <Bell className="w-4 h-4" />
          Notifications actives
        </>
      ) : (
        <>
          <BellOff className="w-4 h-4" />
          Activer les notifications
        </>
      )}
    </Button>
  );
};

export default PWAInstallPrompt;
